// packages/web-ui/src/components/inline-cards/ViewImageModal.tsx
//
// Focused image / diagram VIEW — a full-viewport, light-frosted takeover that
// opens when a response-view image or diagram is expanded from chat. It replaces
// the previous cramped centered dialog with a ChatGPT-style focused surface: the
// image / diagram dominates the canvas, chrome floats minimally (a translucent
// top bar with the title + Close, plus Save in diagram mode), and — when an edit
// handler is wired — a single rounded prompt pill floats at the bottom for
// natural-language edits. Esc / backdrop-click closes.
//
// Two modes share the chrome:
//   • Image mode (`layout === null` + `imageSrc`) — a large centred raster with
//     the non-dismissible illustrative badge; the bottom pill (when
//     `onEditRequest` is wired) restyles / regenerates the picture in place.
//   • Diagram mode (`layout !== null`) — the full `<ViewRenderer>` (same as the
//     Views tab) with `enableZoom` so users can wheel-zoom / pan large trees.
//     `onSave` surfaces a Save button; `onEditRequest` hosts the diagram-agent
//     composer (add/remove KB elements shown here, hide/collapse, re-arrange).
//
// Diagram mode ALSO hosts the opt-in LLM raster IN PLACE, mirroring the Views
// tab's `<ViewActionBar>` + `<ViewRasterSurface>` (RB ruling 2026-07-07): when
// the LIVE `views.llmImage.enabled` master switch is ON and a generate handler
// is wired (`llmImageEnabled` + `onGenerateImage`), a "Generate AI image" button
// sits in the top bar. Clicking it generates a raster of THIS view and switches
// the canvas
// to a Diagram ⇄ AI image toggle so the illustrative render shows without leaving
// the focused view. The deterministic sketch is always the default lead surface
// (the authoritative comprehension surface); the AI image carries the same
// non-dismissible illustrative badge; the SysML source stays one action away via
// the hosting card. When the master switch is OFF the raster subtree is absent
// and the modal is byte-identical to the sketch-only path.
//
// The visual language stays inside the app's light Audi palette (white surfaces,
// hairline borders, Audi-red primary action) but leans premium: soft-rounded
// frames, hairline rings, gentle elevation, and a spring-in entrance
// (`.tessa-focus-*` in global.css, both reduced-motion aware).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import type { ReactFlowLayoutJson } from '@tessa-lean/base/api-schemas/index.js';
import { resolveHiddenNodeIds, defaultViewScope } from '@tessa-lean/base';
import {
  ViewRenderer,
  type ViewRendererHandle,
} from '../../panels/views/ViewRenderer.js';
import { IMAGE_GEN_TIMEOUT_MS } from './raster-timeout.js';
import {
  GeneratingViewState,
  GENERATING_CANVAS_WIDTH,
  GENERATING_CANVAS_HEIGHT,
} from '../loading/GeneratingViewState.js';
import { useReducedMotion } from '../../AppShell/useReducedMotion.js';

/**
 * IV3-S2 — the specific, non-dismissible illustrative-badge copy. NEVER says
 * "verified correct"; explicitly names the unverified facets (positions,
 * labels, arrows, connections) and points to the SysML source for decisions.
 */
export const ILLUSTRATIVE_BADGE_COPY =
  'Illustrative — positions, labels, arrows and connections are NOT verified; ' +
  'use the SysML source for decisions';

export interface ViewImageModalProps {
  open: boolean;
  layout: ReactFlowLayoutJson | null;
  viewId: string;
  viewName: string;
  onClose: () => void;
  /**
   * Optional save handler. When provided, the diagram view becomes editable
   * (`<ViewRenderer readOnly={false}>`) and surfaces a Save button in the top
   * bar. The handler receives the current layout snapshot and returns
   * `{ ok: true }` on success or `{ ok: false, message }` on failure.
   * Omitted → view is read-only (drag disabled, no Save button).
   */
  onSave?: (
    layout: ReactFlowLayoutJson,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  /**
   * IV3-S2 — image mode. When set (and `layout` is null) the view renders a
   * large centred raster `<img>` instead of the ReactFlow canvas, with the
   * non-dismissible illustrative badge. The diagram-layout path is unchanged —
   * this branch only activates when `imageSrc` is provided.
   */
  imageSrc?: string;
  /** Alt text for the raster image (derived from SceneDescription). */
  imageAlt?: string;
  /**
   * Natural-language edit handler. When provided, the focused view hosts a
   * floating bottom prompt pill scoped to this view. In diagram mode it drives
   * the diagram agent ("add the elements connected to the BatteryPack", "hide
   * the radiator", "move the BMS up"); in image mode it restyles / regenerates
   * the picture. The handler resolves once the request was accepted/rejected —
   * the actual re-render arrives out-of-band (the hosting card re-fetches on the
   * `kb:viewRendered` / `image:genReady` re-emit and the fresh image/layout
   * flows back in as a prop). Omitted → no prompt (back-compat for read-only
   * callers/tests).
   */
  onEditRequest?: (
    request: string,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;

  // ── Diagram-mode LLM raster (opt-in, mirrors the Views tab) ────────────────

  /**
   * The LIVE `views.llmImage.enabled` master switch (the caller reads it via
   * `useLlmImageEnabled()`, like the Views tab). When `true` (and
   * `onGenerateImage` is wired) the diagram-mode modal shows the Generate button
   * + the Diagram ⇄ AI image toggle. Anything else ⇒ the raster subtree is
   * absent (byte-identical sketch-only path). Deliberately NOT the envelope's
   * per-view `rasterOffered` eligibility — the button appears whenever raster is
   * enabled for the workspace, matching the Views tab.
   */
  llmImageEnabled?: boolean;
  /**
   * Request an LLM raster of THIS view (already bound to `viewId` by the
   * caller). The optional `wishes` ride in as an additive scene-content
   * instruction (layered over the KB scene server-side); a plain generate omits
   * it. Returns the 202-accept Result — the produced raster lands out-of-band
   * and flows back in as a fresh `rasterImageSrc`. Omitted ⇒ no Generate/regen.
   */
  onGenerateImage?: (
    wishes?: string,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  /**
   * The generated raster's URL for THIS view (from the image-views slice via the
   * hosting card). Present once a raster is ready. Drives the in-place "AI
   * image" surface + reveals the Diagram ⇄ AI image toggle.
   */
  rasterImageSrc?: string;
  /** Alt text for the in-place raster surface. */
  rasterImageAlt?: string;
  /**
   * `true` while a raster is generating for this view (image-views
   * `status === 'generating'`), so the in-place spinner shows even before the
   * local generate round-trip / SSE `image:genStarted` lands.
   */
  rasterGenerating?: boolean;
  /**
   * `true` when the image-views slice reports this view's raster errored /
   * filtered (`status === 'error' | 'filtered'`). Threaded so a modal-initiated
   * generate that fails ASYNCHRONOUSLY (a silent fallback / `image:genFailed`,
   * which never re-defines `rasterImageSrc`) resolves the in-place spinner
   * immediately instead of hanging until the client timeout backstop.
   */
  rasterError?: boolean;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type EditStatus = 'idle' | 'submitting' | 'submitted' | 'error';
/** Which representation the diagram-mode canvas currently shows. */
type ShownEngine = 'diagram' | 'image';
/** Local lifecycle of a raster generate/regenerate round-trip started here. */
type GenStatus = 'idle' | 'generating' | 'error';

export function ViewImageModal({
  open,
  layout,
  viewId,
  viewName,
  onClose,
  onSave,
  imageSrc,
  imageAlt,
  onEditRequest,
  llmImageEnabled,
  onGenerateImage,
  rasterImageSrc,
  rasterImageAlt,
  rasterGenerating,
  rasterError,
}: ViewImageModalProps): JSX.Element | null {
  const closeRef = useRef<HTMLButtonElement>(null);
  const rendererRef = useRef<ViewRendererHandle | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editText, setEditText] = useState('');
  const [editStatus, setEditStatus] = useState<EditStatus>('idle');
  const [editError, setEditError] = useState<string | null>(null);

  // Diagram-mode raster state. `shownEngine` is which surface the canvas leads
  // with (default the deterministic sketch); `genStatus`/`genError` track a
  // generate/regenerate round-trip fired from THIS modal; the elapsed/timeout
  // pair backstops the spinner so it can never hang forever (mirrors
  // `<ViewRasterSurface>`).
  const [shownEngine, setShownEngine] = useState<ShownEngine>('diagram');
  const [genStatus, setGenStatus] = useState<GenStatus>('idle');
  const [genError, setGenError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const genStartRef = useRef<number | null>(null);
  const prevRasterSrcRef = useRef<string | undefined>(rasterImageSrc);
  // Freeze the shared Blueprint-skeleton's animation + elapsed ticker under
  // reduced motion (mirrors `<ViewRasterSurface>`).
  const reducedMotion = useReducedMotion();

  // A raster has "failed" when the local round-trip errored OR the image-views
  // slice reports the view errored/filtered. A raster is "in flight" while the
  // local round-trip is generating OR the slice reports it generating (SSE
  // `image:genStarted`) — but a failure signal always wins, so a spinner never
  // outlives an `image:genFailed` (the async-hang bug).
  const rasterFailed = genStatus === 'error' || rasterError === true;
  const rasterInFlight =
    !rasterFailed && (genStatus === 'generating' || rasterGenerating === true);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  // Reset the transient raster state to the sketch default each time the modal
  // opens, so re-opening never carries a stale AI-image lead / error across
  // views. (The persisted raster itself flows back in via `rasterImageSrc`.)
  useEffect(() => {
    if (open) {
      setShownEngine('diagram');
      setGenStatus('idle');
      setGenError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Full-viewport takeover: lock the page scroll while the focused view is open
  // so the chat behind it can't scroll (and its scrollbar disappears). The
  // portal (below) plus this lock is what makes this a real "new view" rather
  // than a translucent overlay bleeding the chat + its scrollbar through.
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current !== null) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Resolve the local generate spinner the moment a FRESH raster URL arrives.
  // We key on a CHANGE of `rasterImageSrc` (not mere presence): a regenerate
  // retains the prior image under the skeleton, so `rasterImageSrc` is already
  // defined when we set `genStatus='generating'` — only its transition to a new
  // (cache-busted) URL means the produced image landed.
  useEffect(() => {
    if (rasterImageSrc !== prevRasterSrcRef.current) {
      prevRasterSrcRef.current = rasterImageSrc;
      if (rasterImageSrc !== undefined) {
        setGenStatus('idle');
        setGenError(null);
      }
    }
  }, [rasterImageSrc]);

  // Async failure resolution: an `image:genFailed` (or a silent fallback) lands
  // in the slice as `status: 'error' | 'filtered'` and — for a first-time gen —
  // never re-defines `rasterImageSrc`, so the effect above can't clear the local
  // spinner. Flip a still-'generating' local status to 'error' the moment the
  // slice reports the failure so the in-place spinner resolves at once (the
  // sibling IllustrativeImagePanel, which reads the slice directly, already
  // does). Only touches a 'generating' status, so a later successful retry is
  // unaffected.
  useEffect(() => {
    if (rasterError === true) {
      setGenStatus((s) => (s === 'generating' ? 'error' : s));
    }
  }, [rasterError]);

  // Elapsed-time ticker while a raster generates. Under reduced motion it
  // computes once (no per-second ticking) — mirrors `<ViewRasterSurface>`.
  useEffect(() => {
    if (!rasterInFlight) {
      genStartRef.current = null;
      setElapsedMs(0);
      return;
    }
    genStartRef.current ??= Date.now();
    const start = genStartRef.current;
    const tick = (): void => setElapsedMs(Math.max(0, Date.now() - start));
    tick();
    if (reducedMotion) return;
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [rasterInFlight, reducedMotion]);

  // Client-side timeout backstop — motion-independent, so the "taking longer"
  // state can NEVER hang even under reduced motion (where the ticker is frozen).
  useEffect(() => {
    if (!rasterInFlight) {
      setTimedOut(false);
      return;
    }
    genStartRef.current ??= Date.now();
    const remaining =
      IMAGE_GEN_TIMEOUT_MS - Math.max(0, Date.now() - genStartRef.current);
    if (remaining <= 0) {
      setTimedOut(true);
      return;
    }
    setTimedOut(false);
    const id = window.setTimeout(() => setTimedOut(true), remaining);
    return () => window.clearTimeout(id);
  }, [rasterInFlight]);

  const handleEditSubmit = useCallback(async () => {
    if (onEditRequest === undefined || editStatus === 'submitting') return;
    const request = editText.trim();
    if (request.length === 0) return;
    setEditStatus('submitting');
    setEditError(null);
    const result = await onEditRequest(request);
    if (result.ok) {
      setEditStatus('submitted');
      setEditText('');
    } else {
      setEditStatus('error');
      setEditError(result.message);
    }
  }, [onEditRequest, editStatus, editText]);

  // Fire a plain (no-wishes) raster generation and switch the canvas to the AI
  // image surface so the spinner shows in place. On a rejected 202-accept we
  // flip to an inline error; on success the produced raster arrives as a fresh
  // `rasterImageSrc` (the effect above clears the spinner).
  const handleGenerate = useCallback(async () => {
    if (onGenerateImage === undefined || genStatus === 'generating') return;
    setShownEngine('image');
    setGenStatus('generating');
    setGenError(null);
    const result = await onGenerateImage();
    if (!result.ok) {
      setGenStatus('error');
      setGenError(result.message);
    }
  }, [onGenerateImage, genStatus]);

  // Regenerate the leading raster from the composer text (an additive restyle
  // instruction, e.g. "make it black and white"). Keeps the prior image visible
  // under the spinner. Requires non-empty text — a plain regenerate uses the
  // Generate button.
  const handleRegenerate = useCallback(async () => {
    if (onGenerateImage === undefined || rasterInFlight) return;
    const wishes = editText.trim();
    if (wishes.length === 0) return;
    setGenStatus('generating');
    setGenError(null);
    const result = await onGenerateImage(wishes);
    if (result.ok) {
      // Clear the composer ONLY on a successful accept — mirrors
      // handleEditSubmit so a rejected regenerate keeps the user's typed wishes
      // instead of blanking the box under the error.
      setEditText('');
    } else {
      setGenStatus('error');
      setGenError(result.message);
    }
  }, [onGenerateImage, rasterInFlight, editText]);

  const handleSave = useCallback(async () => {
    if (onSave === undefined || rendererRef.current === null) return;
    setSaveStatus('saving');
    setSaveError(null);
    const snapshot = rendererRef.current.getLayout();
    const result = await onSave(snapshot);
    if (toastTimerRef.current !== null) clearTimeout(toastTimerRef.current);
    if (result.ok) {
      setSaveStatus('saved');
      toastTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2400);
    } else {
      setSaveStatus('error');
      setSaveError(result.message);
      toastTimerRef.current = setTimeout(() => {
        setSaveStatus('idle');
        setSaveError(null);
      }, 4000);
    }
  }, [onSave]);

  // Switch the leading surface. Clear the composer + its transient status so a
  // stale diagram-edit / restyle instruction doesn't carry across the mode flip
  // (the composer's PURPOSE changes: diagram edit vs raster restyle).
  const handleToggleEngine = useCallback((next: ShownEngine): void => {
    setShownEngine(next);
    setEditText('');
    setEditStatus('idle');
    setEditError(null);
  }, []);

  const onEditKey = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void (shownEngine === 'image' && layout !== null
          ? handleRegenerate()
          : handleEditSubmit());
      }
    },
    [shownEngine, layout, handleRegenerate, handleEditSubmit],
  );

  if (!open) return null;

  const isImage =
    layout === null && typeof imageSrc === 'string' && imageSrc.length > 0;
  // Open, but neither a raster nor a diagram to show → render nothing.
  if (!isImage && layout === null) return null;

  const isDiagramMode = layout !== null;
  // The diagram-mode raster affordances gate on the LIVE master switch + a wired
  // generate handler — the same gate the Views tab uses, so the button shows for
  // any diagram view whenever raster is enabled for the workspace. Once a raster
  // exists / is generating, the toggle appears regardless (so a view rastered
  // earlier can still be viewed if the switch was later turned off).
  const canGenerate =
    isDiagramMode && llmImageEnabled === true && onGenerateImage !== undefined;
  const rasterReady = rasterImageSrc !== undefined;
  const hasRasterSurface =
    isDiagramMode && (rasterReady || rasterInFlight || rasterFailed);
  const showingRaster = isDiagramMode && shownEngine === 'image';
  // Generate CTA: shown on the sketch surface while no raster exists yet (once
  // one does, the toggle + composer-regenerate take over, so a bare Generate
  // button would double up).
  const showGenerateButton =
    canGenerate && shownEngine === 'diagram' && !rasterReady && !rasterInFlight;

  // Which composer is active + its copy. Over the AI-image surface the composer
  // regenerates the raster with additive wishes; everywhere else it drives the
  // natural-language edit handler (diagram agent, or image restyle in pure-image
  // mode).
  const composerKind: 'edit' | 'regen' | null = showingRaster
    ? onGenerateImage !== undefined
      ? 'regen'
      : null
    : onEditRequest !== undefined
      ? 'edit'
      : null;
  const showComposer = composerKind !== null;
  const composerBusy =
    composerKind === 'regen' ? rasterInFlight : editStatus === 'submitting';
  const composerPlaceholder =
    composerKind === 'regen'
      ? 'Change the AI image — e.g. “make it black and white”, “group by subsystem”'
      : isImage
        ? 'Describe an edit to this image…'
        : 'Edit this diagram — e.g. “add the elements connected to the BatteryPack”, “hide the radiator”, “move the BMS up”';
  const composerAria =
    composerKind === 'regen'
      ? 'Change the AI image'
      : isImage
        ? 'Describe an edit to this image'
        : 'Describe an edit to this diagram';
  const composerSubmitAria =
    composerKind === 'regen'
      ? 'Regenerate AI image'
      : isImage
        ? 'Send image edit request'
        : 'Apply diagram edit';

  // Unified bottom-notice copy (varies by which composer leads).
  let noticeText: string | null = null;
  let noticeIsError = false;
  if (composerKind === 'regen') {
    if (rasterInFlight) {
      noticeText = 'Generating the AI image — it appears here when it’s ready.';
    } else if (rasterFailed) {
      noticeText = genError
        ? `Couldn’t generate the AI image: ${genError}`
        : 'Couldn’t generate the AI image.';
      noticeIsError = true;
    }
  } else if (editStatus === 'submitting') {
    noticeText = 'Working on it…';
  } else if (editStatus === 'submitted') {
    noticeText = 'On its way — the view updates here when it’s ready.';
  } else if (editStatus === 'error') {
    noticeText = editError ? `Couldn’t apply the edit: ${editError}` : null;
    noticeIsError = true;
  }

  const showRasterSpinner = rasterInFlight && !timedOut;

  // Diagram canvas dimensions — fill most of the viewport, leaving room for the
  // floating top bar and (when present) the bottom prompt pill.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const canvasWidth = Math.min(1600, vw - 48);
  const canvasHeight = Math.min(1000, vh - (showComposer ? 210 : 130));

  // Faithfulness signal for the diagram view: how many parts the current scope
  // hides (0 when unscoped). Recomputes whenever a fresh `layout` flows in after
  // an in-place edit, so hiding a part surfaces this notice here too — not only
  // on the inline chat card.
  const hiddenCount =
    layout !== null
      ? resolveHiddenNodeIds(layout, layout.viewScope ?? defaultViewScope()).size
      : 0;
  const isScoped = hiddenCount > 0;

  // Shared pill class for the segmented Diagram ⇄ AI image toggle. Apple-style:
  // the active segment is a white pill with a soft shadow; the inactive one is
  // quiet taupe text (mirrors the Views tab's `.tessa-vab__seg`).
  const segClass = (active: boolean): string =>
    `rounded-full px-4 py-1 text-[13px] leading-none transition-all ${
      active
        ? 'bg-cream-50 text-fg font-semibold shadow-sm'
        : 'text-taupe-700 hover:text-fg font-medium'
    }`;

  // Backdrop / dialog roles: the outer element is a light-frosted takeover; a
  // click on it (but not its children) closes, matching the Esc affordance. It
  // is PORTALLED to <body> so `position: fixed` resolves against the viewport
  // (not a transformed/filtered chat ancestor) and truly covers the app — no
  // chat, second input box, or second scrollbar bleeding through.
  return createPortal(
    <div
      data-testid="view-image-modal"
      role="dialog"
      aria-modal="true"
      aria-label={
        isImage ? `Expanded image: ${viewName}` : `Expanded view: ${viewName}`
      }
      className="tessa-focus-backdrop bg-cream-50/80 fixed inset-0 z-50 flex flex-col backdrop-blur-2xl"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Floating top bar — the exit control sits on the LEFT (X), then the
          title; the AI-image toggle / Generate / Save controls stay on the
          right. */}
      <div className="flex items-center gap-3 px-5 py-3">
        <button
          ref={closeRef}
          type="button"
          data-testid="view-image-modal-close"
          aria-label={isImage ? 'Close expanded image' : 'Close expanded view'}
          onClick={onClose}
          className="border-taupe-300 bg-cream-50/80 hover:border-taupe-900 hover:bg-cream-100 text-fg flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-base leading-none backdrop-blur-md transition-colors"
        >
          <span aria-hidden="true">✕</span>
        </button>
        <h2
          data-testid="view-image-modal-title"
          className="text-fg min-w-0 flex-1 truncate text-sm font-semibold tracking-[-0.01em]"
        >
          {viewName}
        </h2>

        <div className="flex items-center gap-2">
          {/* "Generate AI image" — the prominent opt-in raster CTA (Audi-red
              gradient, matching the Views-tab Generate), pinned to the RIGHT of
              the top bar. Sketch-only + only until a raster exists / begins
              generating: clicking it immediately switches to the AI-image surface
              (spinner there), so this button never shows its own busy state
              (`showGenerateButton` requires `!rasterInFlight`). The Diagram ⇄ AI
              image toggle lives floated over the canvas top-centre (below). */}
          {showGenerateButton ? (
            <button
              type="button"
              data-testid="view-image-modal-generate"
              aria-label="Generate AI image"
              onClick={() => void handleGenerate()}
              style={{
                background: 'linear-gradient(135deg, #bb0a30 0%, #f50537 100%)',
                boxShadow:
                  '0 6px 20px -6px rgba(187, 10, 48, 0.55), 0 1px 2px rgba(0, 0, 0, 0.12)',
              }}
              className="flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-semibold text-white transition-transform hover:scale-[1.03] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span aria-hidden="true">✨</span>
              Generate AI image
            </button>
          ) : null}

          {/* Save (diagram edit mode). */}
          {onSave !== undefined ? (
            <div className="flex items-center gap-2">
              <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-testid="view-image-modal-toast"
                data-toast-status={saveStatus}
                className={`text-xs ${
                  saveStatus === 'saved'
                    ? 'text-emerald-700'
                    : saveStatus === 'error'
                      ? 'text-accent-red'
                      : 'text-transparent'
                }`}
              >
                {saveStatus === 'saved' ? 'Saved ✓' : null}
                {saveStatus === 'error' && saveError !== null
                  ? `Save failed: ${saveError}`
                  : null}
              </div>
              <button
                type="button"
                data-testid="view-image-modal-save"
                aria-label="Save view"
                onClick={() => void handleSave()}
                disabled={saveStatus === 'saving'}
                className="border-taupe-300 bg-cream-50/80 hover:border-taupe-900 hover:bg-cream-100 text-fg rounded-full border px-4 py-1.5 text-xs font-semibold backdrop-blur-md transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saveStatus === 'saving' ? 'Saving…' : 'Save'}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Centred content — the image / diagram frame. Clicking the empty region
          around it also closes (backdrop affordance). */}
      <div
        className="relative flex flex-1 items-center justify-center overflow-auto px-6 pb-2"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {/* Diagram ⇄ AI image toggle — floated over the canvas TOP-CENTRE
            (Apple-style segmented control, mirroring the Views tab's
            `.tessa-vab__engine`). Appears once a raster exists / is generating. */}
        {hasRasterSurface ? (
          <div
            role="group"
            aria-label="Diagram representation"
            data-testid="view-image-modal-engine-toggle"
            style={{ background: 'rgba(120, 120, 128, 0.16)' }}
            className="absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-0.5 rounded-full p-[3px] shadow-md backdrop-blur-md"
          >
            <button
              type="button"
              data-testid="view-image-modal-engine-diagram"
              aria-pressed={shownEngine === 'diagram'}
              onClick={(): void => handleToggleEngine('diagram')}
              className={segClass(shownEngine === 'diagram')}
            >
              Diagram
            </button>
            <button
              type="button"
              data-testid="view-image-modal-engine-image"
              aria-pressed={shownEngine === 'image'}
              onClick={(): void => handleToggleEngine('image')}
              className={segClass(shownEngine === 'image')}
            >
              AI image
            </button>
          </div>
        ) : null}

        {/* Faithfulness notice — floats over the top of the diagram whenever the
            view is scoped (some parts hidden), so a partial view can never be
            mistaken for the whole model in the focused editor. Reveal them by
            typing "show everything" into the prompt below. Only on the sketch
            surface — the raster carries its own illustrative badge. Sits below the
            top-centre toggle when that is present. */}
        {layout !== null && isScoped && !showingRaster ? (
          <div
            data-testid="view-image-modal-scope-badge"
            role="status"
            className={`border-amber-500 bg-amber-50 text-amber-900 absolute left-1/2 z-10 -translate-x-1/2 rounded-full border px-3 py-1 text-center text-[10px] font-semibold uppercase tracking-wide shadow-sm ${
              hasRasterSurface ? 'top-14' : 'top-2'
            }`}
          >
            {hiddenCount} {hiddenCount === 1 ? 'part' : 'parts'} hidden — say “show
            everything” to reveal
          </div>
        ) : null}

        {showingRaster ? (
          /* Diagram mode, AI-image surface — the in-place raster. */
          <figure
            data-testid="view-image-modal-raster-surface"
            className="tessa-focus-panel m-0 flex max-h-full flex-col items-center gap-3"
          >
            {rasterReady ? (
              <figcaption
                data-testid="view-image-modal-illustrative-badge"
                className="border-amber-500 bg-amber-50 text-amber-900 max-w-2xl rounded-full border px-3 py-1 text-center text-[10px] font-semibold uppercase tracking-wide"
              >
                {ILLUSTRATIVE_BADGE_COPY}
              </figcaption>
            ) : null}
            <div className="relative">
              {rasterReady ? (
                <div
                  data-testid="view-image-modal-raster-wrap"
                  className="ring-taupe-300/60 overflow-hidden rounded-2xl bg-white shadow-lg ring-1"
                >
                  <img
                    data-testid="view-image-modal-raster-img"
                    src={rasterImageSrc}
                    alt={rasterImageAlt ?? `${viewName} (illustrative)`}
                    className="block max-h-[70vh] max-w-[92vw] object-contain"
                  />
                </div>
              ) : null}

              {showRasterSpinner ? (
                // Shared Blueprint-skeleton generating state (#63): `canvas` for a
                // first generation (block-sized card), `overlay` for a regenerate
                // over the retained prior image. The host owns the elapsed ticking
                // + reduced-motion freeze; test ids preserve this modal's contract.
                <GeneratingViewState
                  variant={rasterReady ? 'overlay' : 'canvas'}
                  elapsedMs={elapsedMs}
                  reducedMotion={reducedMotion}
                  testIds={{
                    root: 'view-image-modal-raster-generating',
                    elapsed: 'view-image-modal-raster-elapsed',
                  }}
                />
              ) : null}

              {rasterInFlight && timedOut ? (
                <div
                  role="status"
                  aria-live="polite"
                  data-testid="view-image-modal-raster-timeout"
                  className={`${
                    rasterReady
                      ? 'bg-cream-50/85 absolute inset-0'
                      : 'border-taupe-500 bg-cream-50 border'
                  } flex flex-col items-center justify-center gap-1 p-3 text-center`}
                  style={
                    rasterReady
                      ? undefined
                      : {
                          // Match the skeleton frame so the swap never reflows.
                          width: GENERATING_CANVAS_WIDTH,
                          height: GENERATING_CANVAS_HEIGHT,
                          maxWidth: '100%',
                        }
                  }
                >
                  <span className="text-accent-red text-sm">
                    This is taking longer than expected.
                  </span>
                  <span className="text-taupe-700 text-xs">
                    It may have fallen back. Switch to Diagram, or try again.
                  </span>
                </div>
              ) : null}

              {rasterFailed ? (
                <div
                  role="status"
                  aria-live="polite"
                  data-testid="view-image-modal-raster-error"
                  className={`${
                    rasterReady
                      ? 'bg-cream-50/85 absolute inset-0'
                      : 'border-taupe-500 bg-cream-50 border'
                  } flex flex-col items-center justify-center gap-1 p-3 text-center`}
                  style={
                    rasterReady
                      ? undefined
                      : {
                          width: GENERATING_CANVAS_WIDTH,
                          height: GENERATING_CANVAS_HEIGHT,
                          maxWidth: '100%',
                        }
                  }
                >
                  <span className="text-accent-red text-sm">
                    Couldn’t generate the AI image.
                  </span>
                  <span className="text-taupe-700 text-xs">
                    Switch to Diagram, or try again.
                  </span>
                </div>
              ) : null}
            </div>
          </figure>
        ) : layout !== null ? (
          <div
            data-testid="view-image-modal-canvas"
            style={{ width: canvasWidth, height: canvasHeight }}
            className="tessa-focus-panel ring-taupe-300/60 overflow-hidden rounded-2xl bg-white shadow-lg ring-1"
          >
            <ViewRenderer
              ref={rendererRef}
              viewId={viewId}
              layoutOverride={layout}
              fetchLayout={false}
              enableZoom
              readOnly={onSave === undefined}
            />
          </div>
        ) : (
          <figure className="tessa-focus-panel m-0 flex max-h-full flex-col items-center gap-3">
            <figcaption
              data-testid="view-image-modal-illustrative-badge"
              className="border-amber-500 bg-amber-50 text-amber-900 max-w-2xl rounded-full border px-3 py-1 text-center text-[10px] font-semibold uppercase tracking-wide"
            >
              {ILLUSTRATIVE_BADGE_COPY}
            </figcaption>
            <div
              data-testid="view-image-modal-image-wrap"
              className="ring-taupe-300/60 overflow-hidden rounded-2xl bg-white shadow-lg ring-1"
            >
              <img
                data-testid="view-image-modal-image"
                src={imageSrc}
                alt={imageAlt ?? `${viewName} (illustrative)`}
                className="block max-h-[76vh] max-w-[92vw] object-contain"
              />
            </div>
          </figure>
        )}
      </div>

      {/* Floating bottom prompt pill — the single natural-language edit surface.
          ChatGPT-style: borderless input inside a rounded-full frosted capsule
          with a circular send button. Only present when a composer is active
          (diagram edit, image restyle, or AI-image regenerate). */}
      {showComposer ? (
        <div className="px-6 pb-6 pt-1">
          <div className="tessa-focus-panel mx-auto w-full max-w-2xl">
            <div className="ring-taupe-300/70 focus-within:ring-taupe-900/40 flex items-center gap-2 rounded-full bg-white/90 py-2 pl-5 pr-2 shadow-lg ring-1 backdrop-blur-md transition-shadow">
              <input
                data-testid="view-image-modal-edit-input"
                type="text"
                value={editText}
                disabled={composerBusy}
                onChange={(e) => {
                  setEditText(e.target.value);
                  if (editStatus === 'submitted' || editStatus === 'error') {
                    setEditStatus('idle');
                    setEditError(null);
                  }
                }}
                onKeyDown={onEditKey}
                placeholder={composerPlaceholder}
                aria-label={composerAria}
                className="text-fg placeholder:text-taupe-500 min-w-0 flex-1 bg-transparent text-sm focus:outline-none disabled:opacity-60"
              />
              <button
                type="button"
                data-testid="view-image-modal-edit-submit"
                aria-label={composerSubmitAria}
                onClick={() =>
                  void (composerKind === 'regen'
                    ? handleRegenerate()
                    : handleEditSubmit())
                }
                disabled={composerBusy || editText.trim().length === 0}
                className="bg-audi-red hover:bg-audi-red-hover flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base leading-none text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span aria-hidden="true">{composerBusy ? '…' : '↑'}</span>
              </button>
            </div>
            <div
              role="status"
              aria-live="polite"
              data-testid="view-image-modal-edit-notice"
              data-edit-status={composerKind === 'regen' ? genStatus : editStatus}
              className={`mt-2 min-h-4 text-center text-[11px] ${
                noticeIsError ? 'text-accent-red' : 'text-taupe-700'
              }`}
            >
              {noticeText}
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
